import { AUTOMATION_NODE_TYPE, TRIGGER_SOURCE_TYPE } from '@constants/automations';
import { CHANNEL, STATUS } from '@constants/channels';
import { collectSegmentIds, hasEventConditions, hasIncompleteConditions } from './conditions';
import { isCompleteDateTrigger } from './dateTrigger';

export const SUPPORTED_CHANNEL_TYPES = [CHANNEL.SMS, CHANNEL.WHATSAPP, CHANNEL.EMAIL, CHANNEL.WHATSAPP_BUSINESS];

export const isUsableChannel = channel =>
    !!channel && channel.status === STATUS.SUCCESS && SUPPORTED_CHANNEL_TYPES.includes(channel.type);

function getNodeIssues(data, { base, channelsMap, channelsKnown, knownSegmentIds, startsOnDate }) {
    const issues = [];

    const conditionIssues = group => {
        if (hasIncompleteConditions(group)) return ['conditionIncomplete'];
        const missing = knownSegmentIds && [...collectSegmentIds(group)].some(id => !knownSegmentIds.has(id));
        return [
            ...(startsOnDate && hasEventConditions(group) ? ['eventFieldOnDate'] : []),
            ...(missing ? ['segmentMissing'] : []),
        ];
    };

    switch (data.type) {
        case AUTOMATION_NODE_TYPE.TRIGGER: {
            if (data.sourceType === TRIGGER_SOURCE_TYPE.DATE) {
                if (!isCompleteDateTrigger(data.date)) issues.push('triggerDate');
            } else if (!(data.activityTypes || []).length) {
                issues.push('triggerEvents');
            }
            if (knownSegmentIds && (data.segmentIds || []).some(id => !knownSegmentIds.has(id))) {
                issues.push('triggerSegmentMissing');
            }
            issues.push(...conditionIssues(base?.entryConditions));
            break;
        }
        case AUTOMATION_NODE_TYPE.SEND_MESSAGE:
        case AUTOMATION_NODE_TYPE.SEND_SURVEY: {
            const channel = data.channelId ? channelsMap[data.channelId] : null;
            if (!data.channelId) {
                issues.push('channelRequired');
            } else if (channelsKnown && !isUsableChannel(channel)) {
                issues.push('channelUnavailable');
            }

            if (data.type === AUTOMATION_NODE_TYPE.SEND_SURVEY && !data.surveyTemplateId) {
                issues.push('surveyRequired');
            }
            if (data.type === AUTOMATION_NODE_TYPE.SEND_MESSAGE && data.isCoupon && !data.couponTemplateId) {
                issues.push('couponRequired');
            }

            if (isUsableChannel(channel)) {
                if (channel.type === CHANNEL.EMAIL && !data.emailTemplateId) {
                    issues.push('emailTemplateRequired');
                }
                if (
                    channel.type === CHANNEL.WHATSAPP_BUSINESS &&
                    !data.whatsAppTemplateRegistryId &&
                    !data.whatsAppTemplateId
                ) {
                    issues.push('whatsAppTemplateRequired');
                }
                if (
                    data.type === AUTOMATION_NODE_TYPE.SEND_MESSAGE &&
                    (channel.type === CHANNEL.SMS || channel.type === CHANNEL.WHATSAPP) &&
                    !String(data.message || '').trim()
                ) {
                    issues.push('messageRequired');
                }
            }
            break;
        }
        case AUTOMATION_NODE_TYPE.WAIT:
            if (!(data.waitMinutes > 0)) {
                issues.push('waitInvalid');
            }
            break;
        case AUTOMATION_NODE_TYPE.BRANCH:
            issues.push(...conditionIssues(data.conditions));
            break;
        default:
            break;
    }

    return [...new Set(issues)];
}

const SEND_TYPES = [AUTOMATION_NODE_TYPE.SEND_MESSAGE, AUTOMATION_NODE_TYPE.SEND_SURVEY];

function reachableNodeIds(startId, edges) {
    const adjacency = edges.reduce((acc, edge) => {
        (acc[edge.source] ||= []).push(edge.target);
        return acc;
    }, {});

    const visited = new Set([startId]);
    const queue = [startId];
    while (queue.length) {
        for (const next of adjacency[queue.shift()] || []) {
            if (!visited.has(next)) {
                visited.add(next);
                queue.push(next);
            }
        }
    }
    return visited;
}

export function computeGraphIssues({ nodes, edges }, { base, channels, labels }) {
    const knownChannels = channels || [];
    const channelsMap = knownChannels.reduce((acc, channel) => {
        acc[channel.id] = channel;
        return acc;
    }, {});

    const trigger = nodes.find(node => node.data.type === AUTOMATION_NODE_TYPE.TRIGGER);
    const context = {
        base,
        startsOnDate: trigger?.data.sourceType === TRIGGER_SOURCE_TYPE.DATE,
        channelsMap,
        channelsKnown: knownChannels.length > 0,
        knownSegmentIds: labels?.length ? new Set(labels.map(label => label.id)) : null,
    };

    const result = {};
    for (const node of nodes) {
        const issues = getNodeIssues(node.data, context);
        if (issues.length) {
            result[node.id] = issues;
        }
    }

    if (trigger) {
        const reachable = reachableNodeIds(trigger.id, edges || []);
        const reachesSend = nodes.some(node => reachable.has(node.id) && SEND_TYPES.includes(node.data.type));
        if (!reachesSend) {
            result[trigger.id] = [...(result[trigger.id] || []), 'noAction'];
        }
    }

    return result;
}
